import * as React from 'react';
import { EPaymentProvider } from '../../../../../PaymentWidgetMicrofrontend';
import { saveDraftFail } from '../../../../actions/draft';
import { setPaymentProcessingData } from '../../../../actions/paymentProcessing';
import { publishOfferFail } from '../../../../actions/publishInfo';
import { checkPaymentStatusThunk } from '../../../../thunks/payment/checkPaymentStatus';
import { loadPublishTerms } from '../../../../thunks/publishTerms/loadPublishTerms';
import { EPaymentStatus, EPaymentType } from '../../../../types/reducers/paymentProcessing';
import { useDispatch } from 'react-redux';
import { defaultTo } from 'ramda';
import { PUBLISH_PAYMENT_STARTED_EVENT } from '../../../../constants/events';
import { getPaymentType } from './utils';
import { processPublishOfferThunk } from '../../../../thunks/offer/processPublishOffer';
import { checkServiceThunk } from '../../../../thunks/checkout/checkService';

interface ISuccessBalancePaymentHandlerParams {
  paymentId: string;
  provider: EPaymentProvider;
  paymentType?: EPaymentType;
  useAutoPublish?: boolean;
}

interface ISuccessPackagePaymentHandlerParams {
  paymentId: string;
  provider: EPaymentProvider;
  paymentType?: EPaymentType;
}

export const useWebviewEventsHandlers = () => {
  const [isPublishPaymentLaunched, setIsPublishPaymentLaunched] = React.useState(false);

  const dispatch = useDispatch();

  const refillBalanceSuccessHandler = React.useCallback(
    async (params?: ISuccessBalancePaymentHandlerParams) => {
      if (params && typeof params === 'object') {
        const { paymentId, paymentType, useAutoPublish } = params;

        const type = getPaymentType({ paymentType, isPublishPaymentLaunched });
        const provider = defaultTo(EPaymentProvider.YooKassa, params.provider);

        dispatch(setPaymentProcessingData({ paymentId, provider, type }));

        const paymentResult = await dispatch(checkPaymentStatusThunk({ type, paymentId, provider }));

        if (!!useAutoPublish && type === EPaymentType.Publish && paymentResult === EPaymentStatus.Succeeded) {
          await dispatch(checkServiceThunk());

          dispatch(processPublishOfferThunk());
        }
      } else {
        dispatch(checkServiceThunk());
      }
    },
    [dispatch, isPublishPaymentLaunched],
  );

  const refillBalanceCancelHandler = React.useCallback(() => {
    setIsPublishPaymentLaunched(false);
  }, []);

  const publishOfferFailHandler = React.useCallback(() => {
    setIsPublishPaymentLaunched(false);

    dispatch(publishOfferFail());
  }, [dispatch]);

  const saveOfferDraftFailHandler = React.useCallback(() => {
    dispatch(saveDraftFail());
  }, [dispatch]);

  const buyPackageSuccessHandler = React.useCallback(
    (params?: ISuccessPackagePaymentHandlerParams) => {
      // Если есть paymentId – покупка с пополнением/холдированием, проверяем статус платежа
      if (params && typeof params === 'object' && 'paymentId' in params) {
        refillBalanceSuccessHandler({ ...params, paymentType: EPaymentType.Package });
      } else {
        // нет paymentId – покупка со счета пользователя, обновляем publishTerms
        dispatch(loadPublishTerms());
      }
    },
    [dispatch, refillBalanceSuccessHandler],
  );

  const publishPaymentStartedHandler = React.useCallback(() => {
    setIsPublishPaymentLaunched(true);
  }, []);

  React.useEffect(() => {
    window.appInterfaceV2.refillBalanceSuccess = refillBalanceSuccessHandler;
    window.appInterfaceV2.refillBalanceCancel = refillBalanceCancelHandler;
    window.appInterfaceV2.publishOfferFail = publishOfferFailHandler;
    window.appInterfaceV2.saveOfferDraftFail = saveOfferDraftFailHandler;
    window.appInterfaceV2.buyPackageSuccess = buyPackageSuccessHandler;

    window.addEventListener(PUBLISH_PAYMENT_STARTED_EVENT, publishPaymentStartedHandler);

    return () => {
      delete window.appInterfaceV2.refillBalanceSuccess;
      delete window.appInterfaceV2.refillBalanceCancel;
      delete window.appInterfaceV2.publishOfferFail;
      delete window.appInterfaceV2.saveOfferDraftFail;
      delete window.appInterfaceV2.buyPackageSuccess;

      window.removeEventListener(PUBLISH_PAYMENT_STARTED_EVENT, publishPaymentStartedHandler);
    };
  }, [
    buyPackageSuccessHandler,
    refillBalanceCancelHandler,
    publishOfferFailHandler,
    publishPaymentStartedHandler,
    refillBalanceSuccessHandler,
    saveOfferDraftFailHandler,
  ]);
};
